/**
 * Shape recognition for hold-to-snap.
 *
 * Hold still at the end of a stroke and the freehand wobble is replaced by the
 * shape you were clearly aiming for — the trick iOS plays with Scribble.
 *
 * Everything here is pure and works on the flat `[x, y, pressure, ...]` point
 * array, so the rules can be unit-tested directly rather than inferred from
 * what the canvas happened to draw.
 *
 * The bias throughout is towards *not* snapping. A wrong snap destroys work
 * somebody meant to keep, while a missed snap costs them one more attempt.
 */

/** Angles within this of a right angle still count as a corner. */
const CORNER_TOLERANCE = Math.PI / 5; // 36°
/** How near an axis a line has to be before it is straightened onto it. */
const AXIS_SNAP = (7 * Math.PI) / 180;

function xy(points) {
  const out = [];
  for (let i = 0; i < points.length; i += 3) out.push([points[i], points[i + 1]]);
  return out;
}

function bounds(pts) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

function pathLength(pts) {
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) total += dist(pts[i - 1], pts[i]);
  return total;
}

/** Perpendicular distance from p to the infinite line through a and b. */
function lineDistance(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return dist(p, a);
  return Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / len;
}

/** Douglas–Peucker, returning the kept points. */
function simplify(pts, tolerance) {
  if (pts.length < 3) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = 1;
  keep[pts.length - 1] = 1;
  const stack = [[0, pts.length - 1]];

  while (stack.length) {
    const [first, last] = stack.pop();
    let maxD = -1;
    let idx = -1;
    for (let i = first + 1; i < last; i += 1) {
      const d = lineDistance(pts[i], pts[first], pts[last]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > tolerance && idx > 0) {
      keep[idx] = 1;
      stack.push([first, idx], [idx, last]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

/** Interior angle at b, in radians. */
function angleAt(a, b, c) {
  const v1 = [a[0] - b[0], a[1] - b[1]];
  const v2 = [c[0] - b[0], c[1] - b[1]];
  const l1 = Math.hypot(v1[0], v1[1]);
  const l2 = Math.hypot(v2[0], v2[1]);
  if (l1 < 1e-9 || l2 < 1e-9) return Math.PI;
  const cos = Math.min(1, Math.max(-1, (v1[0] * v2[0] + v1[1] * v2[1]) / (l1 * l2)));
  return Math.acos(cos);
}

/**
 * Try to read a shape out of a freehand stroke.
 *
 * Returns `{ shape, x0, y0, x1, y1 }` in world coordinates, or null when the
 * stroke does not confidently look like anything.
 */
export function recogniseShape(points, { minSize = 12 } = {}) {
  const pts = xy(points);
  if (pts.length < 6) return null;

  const box = bounds(pts);
  const w = box.maxX - box.minX;
  const h = box.maxY - box.minY;
  const diag = Math.hypot(w, h);
  if (diag < minSize) return null;

  const start = pts[0];
  const end = pts[pts.length - 1];
  const span = dist(start, end);
  const length = pathLength(pts);
  if (length < 1e-6) return null;

  // --- straight line ------------------------------------------------------
  // A line barely deviates from its own chord, and its path is barely longer
  // than the chord. Both tests together reject a shallow arc.
  let maxDev = 0;
  for (const p of pts) maxDev = Math.max(maxDev, lineDistance(p, start, end));
  if (span > minSize && maxDev / span < 0.045 && length / span < 1.06) {
    let [x0, y0] = start;
    let [x1, y1] = end;
    const angle = Math.atan2(y1 - y0, x1 - x0);
    // Nudge near-horizontal and near-vertical lines onto the axis, the way a
    // ruler would. Anything else keeps the angle it was drawn at.
    const quarter = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
    if (Math.abs(angle - quarter) < AXIS_SNAP) {
      const len = span;
      x1 = x0 + Math.cos(quarter) * len;
      y1 = y0 + Math.sin(quarter) * len;
    }
    return { shape: 'line', x0, y0, x1, y1 };
  }

  // Everything below is a closed shape, so the ends have to meet.
  const closed = span < diag * 0.3;
  if (!closed) return null;
  if (w < minSize || h < minSize) return null;

  // --- ellipse / circle ---------------------------------------------------
  // Every point sits about the same distance from the centre, once the two
  // axes are normalised.
  const cx = (box.minX + box.maxX) / 2;
  const cy = (box.minY + box.maxY) / 2;
  const rx = w / 2;
  const ry = h / 2;
  let radialError = 0;
  for (const [x, y] of pts) {
    const nx = (x - cx) / rx;
    const ny = (y - cy) / ry;
    radialError += Math.abs(Math.hypot(nx, ny) - 1);
  }
  radialError /= pts.length;

  // --- corner count -------------------------------------------------------
  const simplified = simplify(pts, diag * 0.055);
  // A closed shape repeats its first point at the end; drop the duplicate.
  const verts = simplified.slice();
  if (verts.length > 2 && dist(verts[0], verts[verts.length - 1]) < diag * 0.2) verts.pop();

  const corners = [];
  for (let i = 0; i < verts.length; i += 1) {
    const a = verts[(i - 1 + verts.length) % verts.length];
    const b = verts[i];
    const c = verts[(i + 1) % verts.length];
    // Ignore vertices that are barely a bend at all.
    if (angleAt(a, b, c) < Math.PI - 0.35) corners.push(b);
  }

  if (radialError < 0.11 && corners.length > 5) {
    return { shape: 'ellipse', x0: box.minX, y0: box.minY, x1: box.maxX, y1: box.maxY };
  }

  // --- triangle -----------------------------------------------------------
  if (corners.length === 3 && radialError > 0.1) {
    return { shape: 'triangle', x0: box.minX, y0: box.minY, x1: box.maxX, y1: box.maxY };
  }

  // --- rectangle ----------------------------------------------------------
  if (corners.length === 4) {
    // Only straighten a box that was already drawn roughly upright. Snapping a
    // deliberately tilted one onto the axes would be a surprise, not a help.
    let squareish = true;
    for (let i = 0; i < 4; i += 1) {
      const a = corners[i];
      const b = corners[(i + 1) % 4];
      const edge = Math.atan2(b[1] - a[1], b[0] - a[0]);
      const nearest = Math.round(edge / (Math.PI / 2)) * (Math.PI / 2);
      if (Math.abs(edge - nearest) > CORNER_TOLERANCE) { squareish = false; break; }
    }
    if (squareish) {
      return { shape: 'rect', x0: box.minX, y0: box.minY, x1: box.maxX, y1: box.maxY };
    }
    // A tilted quadrilateral with four clean corners is still a diamond if its
    // corners sit near the middle of each edge of the bounding box.
    const mids = [[cx, box.minY], [box.maxX, cy], [cx, box.maxY], [box.minX, cy]];
    const matched = mids.every((m) => corners.some((c) => dist(c, m) < diag * 0.18));
    if (matched) return { shape: 'diamond', x0: box.minX, y0: box.minY, x1: box.maxX, y1: box.maxY };
  }

  return null;
}

/**
 * A near-circle drawn inside a near-square box should become a true circle
 * rather than a slightly-off ellipse. Returns the box to use.
 */
export function squareUp(result, tolerance = 0.14) {
  if (!result || (result.shape !== 'ellipse' && result.shape !== 'rect')) return result;
  const w = Math.abs(result.x1 - result.x0);
  const h = Math.abs(result.y1 - result.y0);
  const mean = (w + h) / 2;
  if (mean < 1e-9 || Math.abs(w - h) / mean > tolerance) return result;

  const cx = (result.x0 + result.x1) / 2;
  const cy = (result.y0 + result.y1) / 2;
  return {
    ...result,
    x0: cx - mean / 2,
    y0: cy - mean / 2,
    x1: cx + mean / 2,
    y1: cy + mean / 2,
  };
}
